import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  RefreshCw,
  Building2,
  Wrench,
  PoundSterling,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const ContractorJobOffers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [acceptingId, setAcceptingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);

  const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs/available`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      } else {
        toast.error('Failed to load job offers');
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load job offers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleAccept = async (jobId) => {
    setAcceptingId(jobId);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs/${jobId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      if (response.ok) {
        toast.success('Job accepted successfully!');
        navigate(`/contractor/jobs/${jobId}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to accept job');
      }
    } catch (error) {
      toast.error('Failed to accept job');
    } finally {
      setAcceptingId(null);
    }
  };

  const handleDecline = async (jobId) => {
    setDecliningId(jobId);
    try {
      const response = await fetch(`${API_URL}/api/contractors/jobs/${jobId}/decline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: 'Declined by contractor' })
      });
      
      if (response.ok) {
        toast.success('Job declined');
        fetchJobs();
      } else {
        toast.error('Failed to decline job');
      }
    } catch (error) {
      toast.error('Failed to decline job');
    } finally {
      setDecliningId(null);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'emergency': return 'bg-red-100 text-red-700 border-red-200';
      case 'urgent': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'routine': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="contractor-job-offers">
      {/* Header */}
      <div className="mb-6">
        <Link to="/contractor/dashboard" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Available Job Offers</h1>
            <p className="text-slate-600">Jobs matching your trades and service area</p>
          </div>
          <Button variant="outline" onClick={fetchJobs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : jobs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No job offers available</h3>
            <p className="text-slate-500 mt-1">
              New jobs matching your profile will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <Card key={job.id} className="hover:shadow-md transition-shadow" data-testid={`job-offer-${job.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Wrench className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">
                          {job.title || 'Maintenance Request'}
                        </h3>
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {job.category && (
                            <Badge variant="outline" className="text-xs">
                              {job.category}
                            </Badge>
                          )}
                          {job.priority && (
                            <Badge className={`text-xs ${getPriorityColor(job.priority)}`}>
                              {job.priority}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {job.trade}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-slate-600 mt-3 line-clamp-2">
                      {job.description}
                    </p>

                    <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.property_address || job.property_postcode || 'Location TBC'}
                      </div>
                      {job.estimated_cost && (
                        <div className="flex items-center gap-1">
                          <PoundSterling className="h-4 w-4" />
                          Est. £{job.estimated_cost}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        Offered {new Date(job.offered_at).toLocaleDateString('en-GB')}
                      </div>
                    </div>

                    {/* Photos */}
                    {job.photos && job.photos.length > 0 && (
                      <div className="flex gap-2 mt-3">
                        {job.photos.slice(0, 3).map((photo, idx) => (
                          <div key={idx} className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden">
                            <img src={photo.url || photo} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                        {job.photos.length > 3 && (
                          <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center text-sm text-slate-500">
                            +{job.photos.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 lg:items-end">
                    <Button
                      onClick={() => handleAccept(job.id)}
                      disabled={acceptingId === job.id}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`accept-job-${job.id}`}
                    >
                      {acceptingId === job.id ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                      )}
                      Accept Job
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDecline(job.id)}
                      disabled={decliningId === job.id}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      {decliningId === job.id ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Decline
                    </Button>
                    <Link to={`/contractor/jobs/${job.id}`}>
                      <Button variant="ghost" size="sm">
                        View Details <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractorJobOffers;
